import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import {
  CheckCircle as OkIcon,
  ErrorOutline as ErrorIcon,
  RemoveCircleOutline as SkipIcon,
  RadioButtonUnchecked as NaIcon,
  ArrowForward as ArrowIcon,
  Storage as ServerIcon,
} from '@mui/icons-material';
import type { ComponentType } from 'react';
import type { SvgIconProps } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { StageOutcome, StageStatus } from '@/types/backend';

/**
 * Two-server view of an API call:
 *
 *   tenant ──►  ┌─ gjabdmconnector ─┐  ──►  ┌── abdm ──┐
 *                │ gate ✓ · process ✓ │      │     ✗    │
 *                └─ OK ────────────────┘      └─ ERROR ─┘
 *
 * The diagnostic question is always "where did it break — our connector
 * or the upstream ABDM gateway?" so each server gets its own card with a
 * single, large status badge. The connector card additionally surfaces
 * the two sub-stages it actually performs (tenant-gate + platform-
 * processing) as smaller pills, so an operator can see WHY the connector
 * failed without losing the server-level summary.
 *
 * Hover any pill for code, message, and duration.
 */
export function StageFlow({
  tenant,
  platform,
  abdm,
  compact = false,
}: {
  tenant: StageOutcome;
  platform: StageOutcome;
  abdm: StageOutcome;
  compact?: boolean;
}) {
  const theme = useTheme();
  // The connector "server" status is the worse of its two sub-stages.
  // ERROR > SKIPPED > N/A > OK. (OK only when both passed cleanly.)
  const connectorOutcome = combineConnector(tenant, platform);

  return (
    <Stack direction="row" spacing={compact ? 0.75 : 1.25} alignItems="center" sx={{ flexWrap: 'nowrap' }}>
      <TenantPill compact={compact} />
      <FlowArrow tone={statusTone(theme, connectorOutcome.status)} compact={compact} />
      <ServerCard
        name="gjabdmconnector"
        outcome={connectorOutcome}
        substages={[
          { label: 'gate',    outcome: tenant   },
          { label: 'process', outcome: platform },
        ]}
        compact={compact}
      />
      <FlowArrow tone={statusTone(theme, abdm.status)} compact={compact} />
      <ServerCard
        name="abdm"
        outcome={abdm}
        compact={compact}
      />
    </Stack>
  );
}

function TenantPill({ compact }: { compact: boolean }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        px: compact ? 1 : 1.25,
        py: compact ? 0.5 : 0.75,
        borderRadius: theme.radius.sm + 'px',
        border: `1px dashed ${theme.palette.border.subtle}`,
        bgcolor: 'transparent',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontSize: compact ? 11 : 12,
          fontFamily: 'JetBrains Mono, monospace',
          color: theme.palette.text.secondary,
          fontWeight: 500,
          whiteSpace: 'nowrap',
        }}
      >
        tenant
      </Typography>
    </Box>
  );
}

function ServerCard({ name, outcome, substages, compact }: {
  name: string;
  outcome: StageOutcome;
  substages?: { label: string; outcome: StageOutcome }[];
  compact: boolean;
}) {
  const theme = useTheme();
  const tone = statusTone(theme, outcome.status);
  const Icon = iconForStatus(outcome.status);

  return (
    <Tooltip
      title={
        <Box sx={{ p: 0.5 }}>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
            {name}: {outcome.status}
          </Typography>
          {outcome.code && (
            <Typography variant="caption" sx={{ display: 'block', fontFamily: 'JetBrains Mono, monospace' }}>
              {outcome.code}
            </Typography>
          )}
          {outcome.message && (
            <Typography variant="caption" sx={{ display: 'block' }}>
              {outcome.message}
            </Typography>
          )}
        </Box>
      }
      arrow
      placement="top"
    >
      <Box
        sx={{
          minWidth: compact ? 150 : 190,
          px: compact ? 1 : 1.5,
          py: compact ? 0.75 : 1,
          borderRadius: theme.radius.sm + 'px',
          border: `2px solid ${tone}`,
          bgcolor: tone + '0F',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: substages ? 0.5 : 0 }}>
          <ServerIcon sx={{ fontSize: compact ? 14 : 16, color: tone }} />
          <Typography
            variant="caption"
            sx={{
              fontSize: compact ? 11 : 12,
              fontFamily: 'JetBrains Mono, monospace',
              color: theme.palette.text.primary,
              fontWeight: 600,
              flexGrow: 1,
              whiteSpace: 'nowrap',
            }}
          >
            {name}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.25}>
            <Icon sx={{ fontSize: compact ? 14 : 16, color: tone }} />
            <Typography
              variant="caption"
              sx={{ fontSize: compact ? 10 : 11, color: tone, fontWeight: 700 }}
            >
              {outcome.status}
            </Typography>
          </Stack>
        </Stack>

        {substages && (
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
            {substages.map((s) => (
              <SubstagePill key={s.label} label={s.label} outcome={s.outcome} compact={compact} />
            ))}
          </Stack>
        )}

        {!substages && outcome.code && (
          <Typography
            variant="caption"
            sx={{
              fontSize: compact ? 9.5 : 10,
              fontFamily: 'JetBrains Mono, monospace',
              color: theme.palette.text.tertiary,
              display: 'block',
              mt: 0.25,
            }}
          >
            {outcome.code}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

function SubstagePill({ label, outcome, compact }: {
  label: string; outcome: StageOutcome; compact: boolean;
}) {
  const theme = useTheme();
  const tone = statusTone(theme, outcome.status);
  const Icon = iconForStatus(outcome.status);
  return (
    <Tooltip
      title={
        <Box sx={{ p: 0.25 }}>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
            {label}: {outcome.status}
          </Typography>
          {outcome.code && (
            <Typography variant="caption" sx={{ display: 'block', fontFamily: 'JetBrains Mono, monospace' }}>
              {outcome.code}
            </Typography>
          )}
          {outcome.message && (
            <Typography variant="caption" sx={{ display: 'block' }}>
              {outcome.message}
            </Typography>
          )}
          {outcome.durationMs != null && (
            <Typography variant="caption" sx={{ display: 'block', color: 'text.tertiary' }}>
              {outcome.durationMs} ms
            </Typography>
          )}
        </Box>
      }
      arrow
      placement="bottom"
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.25}
        sx={{
          px: compact ? 0.5 : 0.75,
          py: compact ? 0.125 : 0.25,
          borderRadius: 999,
          bgcolor: tone + '20',
        }}
      >
        <Icon sx={{ fontSize: compact ? 11 : 12, color: tone }} />
        <Typography
          variant="caption"
          sx={{
            fontSize: compact ? 9.5 : 10.5,
            fontFamily: 'JetBrains Mono, monospace',
            color: tone,
            fontWeight: 600,
          }}
        >
          {label}
        </Typography>
      </Stack>
    </Tooltip>
  );
}

function FlowArrow({ tone, compact }: { tone: string; compact: boolean }) {
  return <ArrowIcon sx={{ fontSize: compact ? 18 : 22, color: tone }} />;
}

/** ERROR > SKIPPED > N/A > OK precedence. */
function combineConnector(tenant: StageOutcome, platform: StageOutcome): StageOutcome {
  const order: StageStatus[] = ['ERROR', 'SKIPPED', 'N/A', 'OK'];
  const worse = order.find((s) => tenant.status === s || platform.status === s) ?? 'OK';
  if (worse === 'ERROR') {
    const errored = tenant.status === 'ERROR' ? tenant : platform;
    return {
      status: 'ERROR',
      code: errored.code,
      message: errored.message,
      durationMs: (tenant.durationMs ?? 0) + (platform.durationMs ?? 0),
    };
  }
  return { status: worse };
}

function iconForStatus(s: StageStatus): ComponentType<SvgIconProps> {
  switch (s) {
    case 'OK':      return OkIcon;
    case 'ERROR':   return ErrorIcon;
    case 'SKIPPED': return SkipIcon;
    case 'N/A':
    default:        return NaIcon;
  }
}

function statusTone(theme: Theme, s: StageStatus): string {
  switch (s) {
    case 'OK':      return theme.palette.status.success;
    case 'ERROR':   return theme.palette.status.danger;
    case 'SKIPPED': return theme.palette.text.tertiary;
    case 'N/A':
    default:        return theme.palette.text.disabled;
  }
}
